import Emoji from '@/components/Emoji'
import AddIcons from '@/icons/AddIcons'
import DownIcons from '@/icons/DownIcons'
import MemoryIcons from '@/icons/MemoryIcons'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, Typogaphy } from 'vnetwork-ui'
import VMInstanceItem from './VMInstanceItem'

interface VMSectionProps {
    isPOC?: boolean
    servicePriceModel: string
    title: string
    type: 'vm-instance' | 'vm-lb' | 'vm-other'
    items: any[]
    onAddNew?: () => void
    onEdit?: (index: number, updatedData?: any) => void
    onDelete?: (index: number) => void
    configOptions?: Array<{ name: string; title?: string; price?: number }>
    collapsed?: boolean
    vmName?: string
    vmPackageType?: 'predefined' | 'custom'
}

const VMSection: React.FC<VMSectionProps> = ({
    isPOC = false,
    servicePriceModel,
    title,
    type,
    items = [],
    onAddNew,
    onEdit,
    vmName,
    onDelete,
    configOptions = [],
    collapsed = false,
    vmPackageType = 'predefined',
}) => {
    const { t } = useTranslation()
    const [isCollapsed, setIsCollapsed] = useState(collapsed)

    return (
        <div className="mb-2">
            {/* Section Header */}
            <div className="bg-gray-50 border border-gray-200 rounded-t-md px-4 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <IconButton
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            size="small"
                            className={`${isCollapsed ? 'rotate-180' : ''} transition-transform duration-300`}
                        >
                            <DownIcons className="w-4 h-4" />
                        </IconButton>
                        <div className="flex items-center gap-2">
                            <MemoryIcons className="w-4 h-4 text-primary" />
                            <Typogaphy className="font-medium text-primary">
                                {t(title?.toLowerCase())}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-gray-600">
                                (
                                {t('common.total', {
                                    count: items.length,
                                })}
                                )
                            </Typogaphy>
                        </div>
                    </div>
                    {!(
                        type === 'vm-instance' && vmPackageType === 'custom'
                    ) && (
                        <Button
                            size="small"
                            variant="outlined"
                            type="button"
                            startIcon={<AddIcons className="w-4 h-4" />}
                            onClick={onAddNew}
                        >
                            {!onAddNew
                                ? t('common.add_first', {
                                      title: t(title.toLowerCase()),
                                  })
                                : t('common.add_new', {
                                      title: t(title.toLowerCase()),
                                  })}
                        </Button>
                    )}
                </div>
            </div>
            {!isCollapsed && (
                <div className="border-l border-r border-b border-gray-200 px-4 pt-4">
                    {items.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <div className="text-4xl mb-2">
                                <Emoji emojiKey="general:empty" size="sm" />
                            </div>
                            <Typogaphy className="text-sm text-gray-500">
                                {t('common.no_data', {
                                    title: title.toLowerCase(),
                                })}
                            </Typogaphy>
                        </div>
                    ) : (
                        <div>
                            {items?.map((item, index) => (
                                <VMInstanceItem
                                    servicePriceModel={servicePriceModel}
                                    isPOC={isPOC}
                                    vmName={vmName}
                                    key={`item-${index}`}
                                    type={type}
                                    data={item}
                                    onEdit={(updatedData) =>
                                        onEdit?.(index, updatedData)
                                    }
                                    onDelete={() => onDelete?.(index)}
                                    configOptions={configOptions}
                                    vmPackageType={
                                        type === 'vm-instance'
                                            ? vmPackageType
                                            : 'predefined'
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default VMSection
